import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import DashboardNav from '@/components/DashboardNav';
import LessonsTab from '@/components/LessonsTab';
import ContentTab from '@/components/ContentTab';
import EvaluationsTab from '@/components/EvaluationsTab';
import CommentsTab from '@/components/CommentsTab';
import VoiceRecorderTab from '@/components/VoiceRecorderTab';
import TutorialsTab from '@/components/TutorialsTab';
import LibraryTab from '@/components/LibraryTab';
import AnalyticsTab from '@/components/AnalyticsTab';
import StudentProgressDialog from '@/components/StudentProgressDialog';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, Users, BookOpen, FileText, CheckCircle, ClipboardList, RefreshCw, MessageCircle, UserPlus, Sparkles, Mic, GraduationCap, Library, BarChart3 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { key: 'lessons', icon: BookOpen, color: '#3B82F6' },
  { key: 'tasks', icon: FileText, color: '#F59E0B' },
  { key: 'corrections', icon: CheckCircle, color: '#10B981' },
  { key: 'evaluations', icon: ClipboardList, color: '#EF4444' },
  { key: 'reinforcement', icon: RefreshCw, color: '#8B5CF6' },
  { key: 'comments', icon: MessageCircle, color: '#EC4899' },
  // Fase 3 · retirada la pestaña "voice_recorder": la funcionalidad se
  // integró en el visor PDF sobre las coordenadas que Feedback Hub
  // devuelve para cada `audio_exercise`. Mantener la clave fuera del
  // menú evita que aparezca en tabs sin romper `t('voice_recorder')` en
  // otros lugares (el archivo VoiceRecorderTab sigue existiendo pero
  // ya no está enrutado).
  { key: 'tutorials', icon: GraduationCap, color: '#F97316' },
  { key: 'library', icon: Library, color: '#64748B', teacherOnly: true },
  { key: 'analytics', icon: BarChart3, color: '#0EA5E9', teacherOnly: true },
];

export default function ClassView() {
  const { classId } = useParams();
  const { user, token } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const headers = { headers: { Authorization: `Bearer ${token}` } };
  const [activeTab, setActiveTab] = useState('lessons');
  const [classData, setClassData] = useState(null);
  const [showAddMember, setShowAddMember] = useState(false);
  const [availableStudents, setAvailableStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState('');
  const [showStudentProgress, setShowStudentProgress] = useState(null);
  const isStudent = user?.role === 'student';

  const fetchClass = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/classes/${classId}`, headers);
      setClassData(res.data);
    } catch { toast.error(t('error')); }
  }, [classId, token]);

  useEffect(() => { fetchClass(); }, [fetchClass]);

  const fetchStudents = async () => {
    try {
      const res = await axios.get(`${API}/users`, headers);
      const currentMembers = classData?.members?.map(m => m.user_id) || [];
      setAvailableStudents(res.data.filter(u => u.role === 'student' && !currentMembers.includes(u.id)));
    } catch (err) { console.error("[ClassView] silent error:", err); }
  };

  const handleAddMember = async () => {
    if (!selectedStudent) return;
    try {
      await axios.post(`${API}/classes/${classId}/members`, { user_id: selectedStudent, role: 'student' }, headers);
      toast.success(t('success'));
      setShowAddMember(false);
      setSelectedStudent('');
      fetchClass();
    } catch (err) { toast.error(err.response?.data?.detail || t('error')); }
  };

  const handleRemoveMember = async (userId) => {
    try {
      await axios.delete(`${API}/classes/${classId}/members/${userId}`, headers);
      toast.success(t('success'));
      fetchClass();
    } catch { toast.error(t('error')); }
  };

  if (!classData) return (
    <div className="min-h-screen bg-slate-50">
      <DashboardNav />
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50" data-testid="class-view-page">
      <DashboardNav />

      {/* Class Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard-btn">
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <div>
                <h2 className="text-xl font-bold text-slate-800" style={{ fontFamily: 'Montserrat' }}>{classData.name}</h2>
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  {classData.level && <Badge variant="secondary">{classData.level}</Badge>}
                  <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {classData.member_count} {t('members')}</span>
                </div>
              </div>
            </div>
            {!isStudent && (
              <Button data-testid="add-member-btn" variant="outline" size="sm" className="gap-1 rounded-lg" onClick={() => { setShowAddMember(true); fetchStudents(); }}>
                <UserPlus className="w-4 h-4" /> {t('add_students')}
              </Button>
            )}
          </div>
        </div>

        {/* Tab Bar */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-1 overflow-x-auto pb-0 pt-2 scrollbar-hide" data-testid="tab-bar">
            {TABS.filter(tab => !tab.teacherOnly || !isStudent).map(tab => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  data-testid={`tab-${tab.key}`}
                  onClick={() => setActiveTab(tab.key)}
                  className={`tab-btn flex items-center gap-2 px-4 py-2.5 rounded-t-xl text-sm font-bold whitespace-nowrap ${isActive ? 'active text-white' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'}`}
                  style={isActive ? { backgroundColor: tab.color, color: 'white' } : {}}
                >
                  <Icon className="w-4 h-4" />
                  <span className="hidden sm:inline">{t(tab.key)}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {activeTab === 'lessons' && <LessonsTab classId={classId} />}
        {activeTab === 'tasks' && <ContentTab classId={classId} contentType="task" uploadLabel={t('upload_task')} downloadLabel={t('download_task')} submitLabel={t('submit_task')} reviewLabel={t('review_task')} />}
        {activeTab === 'corrections' && <ContentTab classId={classId} contentType="correction" uploadLabel={t('upload_correction')} downloadLabel={t('download_correction')} submitLabel={t('submit_correction')} reviewLabel={t('review_correction')} />}
        {activeTab === 'evaluations' && <EvaluationsTab classId={classId} />}
        {activeTab === 'reinforcement' && <ContentTab classId={classId} contentType="reinforcement" uploadLabel={t('reinforcement_activity')} downloadLabel={t('download_reinforcement')} submitLabel={t('submit_reinforcement')} reviewLabel={t('review_task')} />}
        {activeTab === 'comments' && <CommentsTab classId={classId} students={classData?.members?.filter(m => m.role === 'student') || []} />}
        {activeTab === 'voice_recorder' && <VoiceRecorderTab classId={classId} students={classData?.members?.filter(m => m.role === 'student') || []} />}
        {activeTab === 'tutorials' && <TutorialsTab classId={classId} students={classData?.members?.filter(m => m.role === 'student') || []} />}
        {activeTab === 'library' && <LibraryTab classId={classId} students={classData?.members?.filter(m => m.role === 'student') || []} />}
        {activeTab === 'analytics' && <AnalyticsTab classId={classId} />}
      </div>

      {/* Members List (shown below content for teacher/admin) */}
      {!isStudent && classData.members && classData.members.length > 0 && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
          <h4 className="text-sm font-bold text-slate-500 mb-3">{t('student_list')}</h4>
          <div className="bg-white rounded-xl border overflow-hidden">
            {classData.members.filter(m => m.role === 'student').map(m => (
              <div key={m.user_id} className="flex items-center justify-between px-4 py-3 border-b last:border-0 hover:bg-slate-50">
                <div>
                  <p className="text-sm font-medium">{m.first_name} {m.last_name}</p>
                  <p className="text-xs text-slate-400">{m.email}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShowStudentProgress(m.user_id)}
                    className="flex items-center gap-1 px-2 py-1 rounded-lg bg-amber-50 text-amber-600 hover:bg-amber-100 text-xs font-bold transition-colors"
                    data-testid={`view-progress-${m.user_id}`}
                  >
                    <Sparkles className="w-3 h-3" /> {t('my_progress')}
                  </button>
                  {m.email_verified ? <Badge className="bg-emerald-100 text-emerald-700 text-xs">{t('validated')}</Badge> : <Badge variant="secondary" className="text-xs">{t('email_not_verified')}</Badge>}
                  <button onClick={() => handleRemoveMember(m.user_id)} className="text-slate-300 hover:text-red-500 transition-colors p-1" data-testid={`remove-member-${m.user_id}`}>
                    <span className="text-xs">&times;</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Student Progress Dialog (for teacher/admin) */}
      <StudentProgressDialog
        open={!!showStudentProgress}
        onClose={() => setShowStudentProgress(null)}
        studentId={showStudentProgress}
        classId={classId}
      />

      {/* Add Member Dialog */}
      <Dialog open={showAddMember} onOpenChange={setShowAddMember}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>{t('add_students')}</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            <Select value={selectedStudent} onValueChange={setSelectedStudent}>
              <SelectTrigger data-testid="select-student-trigger"><SelectValue placeholder={t('search_students')} /></SelectTrigger>
              <SelectContent>
                {availableStudents.map(s => (
                  <SelectItem key={s.id} value={s.id}>{s.first_name} {s.last_name} ({s.username})</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button data-testid="confirm-add-member" onClick={handleAddMember} className="w-full">{t('add')}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
